import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {Nav} from 'react-bootstrap';
import {matchPath, useLocation} from 'react-router-dom';
import {Icon, Loader} from 'shared/helpers';
import {
    isDeviceSmall,
    useIsHandheldPortrait,
    useIsTouchDevices,
} from 'shared/helpers/DeviceSize';
import {useRightSidebar} from 'shared/components/Sidebar';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import excel from 'shared/Excel';
import {Link} from 'shared/reload/Link';
import {
    useVariationResetButton,
    VariationResetButton,
} from 'shared/components/Header/VariationResetButton';
import {isEmpty} from 'lodash';
import {useJobSubmitHandler} from 'components/customer/Job/helpers/useJobSubmitHandler';
import {useGetTotalProductCountQuery} from 'components/customer/Job/store/jobApi';
import {ProductCount} from 'shared/components/Header/CartToggleButton';
import useUI from 'shared/useUI';
import {StyledDiv} from 'shared/components/MenuButton';
import {useJobVariations} from 'shared/components/Header/helper/useJobVariations';
import {SubmitButton} from './SubmitButton';

/**
 * @param {Object} params
 * @param {Object} [params.jobMenu]
 * @param {boolean} [params.isCollapsed]
 * @param {string} [params.className]
 * @return {*}
 */
export const JobMenuItem = ({jobMenu, isCollapsed, className}) => {
    const isSmallDevice = useIsHandheldPortrait();
    const hasCartSidebar = !isDeviceSmall();
    const {toggle: toggleCart} = useRightSidebar();
    const {job, room, actionButtonsEnabled, expiredJobPrice} = useJobContext();
    const {userProfile} = useAppContext();
    const location = useLocation();
    const {handleJobSubmission, loader} = useJobSubmitHandler();
    const {isSmallScreenLayout} = useUI();
    const isTouchDevice = useIsTouchDevices();

    const {data: totalProductCount} = useGetTotalProductCountQuery(
        Number(job?.displayId),
        {
            skip: !job?.displayId,
        }
    );

    const [cutOffMenuList, setCutOffMenuList] = useState(-1);

    const {noVariations, extraVariation} = useJobVariations();
    const {resetJob} = useVariationResetButton();
    const page = useMemo(() => {
        const urlParts = location.pathname.split('/');
        let page = urlParts.pop();
        if (!isNaN(page)) {
            page = urlParts.pop();
        }
        return page;
    }, [location]);

    const conditionValues = useMemo(
        () => ({
            ...userProfile,
            hasRoom: !!room?.id,
            cutOffMenuList,
            page,
            isSmallDevice,
        }),
        [userProfile, room?.id, cutOffMenuList, page, isSmallDevice]
    );

    const resolveUrl = useCallback(
        /**
         * @param {Object} menu
         * @param {string} menu.url
         * @param {string} [menu.alternateUrl]
         * @param {string} [menu.condition]
         * @return {string}
         */
        (menu) => {
            let url = menu.url;

            if (
                menu.alternateUrl &&
                menu.condition &&
                excel.calculate(menu.condition, conditionValues)
            ) {
                url = menu.alternateUrl;
            }

            if (
                job.hasOwnProperty('displayId') &&
                typeof url === 'string' &&
                url.indexOf('{job}') > -1
            ) {
                url = url.replace('{job}', job.displayId);
            }

            if (
                room.hasOwnProperty('id') &&
                typeof url === 'string' &&
                url.indexOf('{room}') > -1
            ) {
                url = url.replace('{room}', room.id);
            }

            return url;
        },
        [conditionValues, job, room]
    );

    const activeKey = useMemo(() => {
        const urlParts = location.pathname.split('/');
        const index = urlParts.indexOf('benchtop-module');
        if (index != -1) {
            return urlParts.splice(0, index + 1).join('/');
        }

        const isDashboard =
            matchPath(
                '/v2/job/:jobId/room/:roomId/dashboard',
                location.pathname
            ) || matchPath('/v2/job/:jobId/dashboard', location.pathname);
        const dashboardMenu = jobMenu.find(
            (menu) => menu.id == 'job_dashboard'
        );
        if (isDashboard && dashboardMenu) {
            return resolveUrl(dashboardMenu);
        }

        return location.pathname;
    }, [location, jobMenu, resolveUrl]);

    useEffect(() => {
        setCutOffMenuList(isSmallDevice ? 5 : -1);
    }, [isSmallDevice]);

    const toggleMenu = jobMenu.find((menu) => menu.toggleButton);
    let filteredJobMenus = jobMenu.filter((menu) => {
        if (menu.toggleButton) {
            return false;
        }

        if (userProfile.show_pricing && expiredJobPrice) {
            return false;
        }

        if (
            isSmallDevice &&
            menu.hasOwnProperty('onlyMobile') &&
            !menu.onlyMobile
        ) {
            return false;
        }

        if (
            !isSmallDevice &&
            menu.hasOwnProperty('onlyMobile') &&
            menu.onlyMobile
        ) {
            return false;
        }

        if (menu.onlyProcessingJob && !actionButtonsEnabled) {
            return false;
        }

        if (typeof menu.isVisible !== 'boolean') {
            menu.isVisible = excel.calculate(menu.isVisible, userProfile);
        }

        if (
            menu.hasOwnProperty('resetVariation') &&
            (job?.accepted == 0 || (!noVariations && !extraVariation))
        ) {
            return false;
        }

        return menu.isVisible;
    });

    const hasMoreMenus = filteredJobMenus.length > 5 && isSmallDevice;
    // the mobile menu keeps the page padded to its collapsed height
    const isExpanded = hasMoreMenus && cutOffMenuList === -1;

    if (hasMoreMenus) {
        filteredJobMenus.splice(4, 0, toggleMenu);
        filteredJobMenus = filteredJobMenus.filter((menu, key) => {
            if (cutOffMenuList > -1 && key >= cutOffMenuList) {
                return false;
            }
            return true;
        });
    }

    if (isEmpty(job)) {
        return null;
    }

    return (
        <>
            <Loader loader={loader} />
            <Nav
                data-expanded={isExpanded}
                activeKey={activeKey}
                className={`${className} ${
                    isSmallDevice ? 'job-menu' : 'flex-column job-menu'
                }`}>
                {filteredJobMenus.map((menu, key) => {
                    let menuName =
                        isSmallDevice && menu.hasOwnProperty('mobileName')
                            ? menu.mobileName
                            : menu.name;
                    let icon = menu.icon;
                    let action = undefined;
                    let navAs = Link;
                    let to = resolveUrl(menu);
                    let isDisabled = false;

                    if (
                        menu.alternateIcon &&
                        menu.condition &&
                        excel.calculate(menu.condition, conditionValues)
                    ) {
                        icon = menu.alternateIcon;
                    }

                    if (menu.text) {
                        menuName = excel.calculate(menu.text, userProfile);
                    }

                    if (
                        menu.alternateText &&
                        menu.condition &&
                        excel.calculate(menu.condition, conditionValues)
                    ) {
                        menuName = menu.alternateText;
                    }

                    if (menuName == '') {
                        menuName = menu.name;
                    }

                    if (
                        menu.hasOwnProperty('toggleButton') &&
                        menu.toggleButton
                    ) {
                        navAs = undefined;
                        to = undefined;
                        action = () => {
                            setCutOffMenuList(cutOffMenuList === -1 ? 5 : -1);
                        };
                    }

                    if (menu.id === 'cart' && hasCartSidebar) {
                        navAs = undefined;
                        to = undefined;
                        action = toggleCart;
                        menuName = menu.name;
                    }

                    if (
                        menu.hasOwnProperty('submitButton') &&
                        menu.submitButton
                    ) {
                        navAs = undefined;
                        to = undefined;
                        action = handleJobSubmission;
                    }

                    if (menu.hasOwnProperty('resetVariation')) {
                        navAs = undefined;
                        to = undefined;
                        action = resetJob;
                    }

                    if (
                        menu.hasOwnProperty('isDisabled') &&
                        typeof menu.isDisabled !== 'boolean' &&
                        !!job?.id
                    ) {
                        const {condition, scope} = menu.isDisabled;
                        isDisabled = excel.calculate(
                            condition,
                            scope === 'job' ? job : userProfile
                        );
                    }

                    let showProductCount = false;
                    if (menuName == 'Cart' || menuName == 'Dashboard') {
                        showProductCount = true;
                    }

                    return (
                        <Nav.Link
                            id={menu.id}
                            key={menu.id}
                            as={navAs}
                            to={to}
                            href={to}
                            onClick={action}
                            disabled={
                                typeof job.id === 'undefined' ||
                                (!room.hasOwnProperty('id') &&
                                    menu.needsRoom) ||
                                isDisabled
                            }
                            style={
                                showProductCount && totalProductCount > 0
                                    ? {position: 'relative'}
                                    : {}
                            }>
                            <StyledDiv
                                $isSmallDevice={
                                    !isTouchDevice && isSmallScreenLayout
                                }>
                                <Icon iconName={icon} title={menuName} />
                            </StyledDiv>
                            {showProductCount && totalProductCount > 0 ? (
                                <ProductCount
                                    style={{top: '-5px', left: '45px'}}>
                                    {totalProductCount}
                                </ProductCount>
                            ) : null}
                            {isCollapsed ? '' : <label>{menuName}</label>}
                        </Nav.Link>
                    );
                })}

                {!isSmallDevice ? (
                    <section className="sidebar-footer-left">
                        <SubmitButton
                            left={true}
                            showLabel={isCollapsed ? false : true}
                        />
                        <VariationResetButton />
                    </section>
                ) : null}
            </Nav>
        </>
    );
};
